import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api, fmtUsd } from "@/lib/api";
import { usePrice } from "@/hooks/usePrice";

const RANGES = [{ k: 1, l: "1D" }, { k: 7, l: "1W" }, { k: 30, l: "1M" }, { k: 365, l: "1YR" }];

const Tip = ({ active, payload }) =>
  active && payload?.length ? (
    <div className="rounded-lg border border-[#212638] bg-[#0F111A] px-3 py-2 text-xs">
      <div className="text-[#94A3B8]">{new Date(payload[0].payload.t).toLocaleString()}</div>
      <div className="font-mono font-semibold">{fmtUsd(payload[0].value)}</div>
    </div>
  ) : null;

export const PriceChart = ({ compact = false }) => {
  const [days, setDays] = useState(1);
  const [hist, setHist] = useState([]);
  const live = usePrice();
  const data = live && hist.length ? [...hist.filter((p) => p.t < live.at), { t: live.at, price: live.price }] : hist;
  const [status, setStatus] = useState("loading");
  const h = compact ? 224 : 288;

  const load = useCallback(() => {
    setStatus("loading");
    api.get(`/price/chart?days=${days}`)
      .then((r) => { setHist(r.data.prices); setStatus(r.data.prices.length ? "ok" : "error"); })
      .catch(() => setStatus("error"));
  }, [days]);
  const baseT = live?.base_t;
  const synced = useRef(null);
  useEffect(() => {
    if (days !== 1 || !baseT || !hist.length || hist[0].t >= baseT || synced.current === baseT) return;
    synced.current = baseT;
    api.get("/price/chart?days=1").then((r) => r.data.prices.length && setHist(r.data.prices)).catch(() => {});
  }, [baseT, days, hist]);

  useEffect(() => {
    load();
    const id = setInterval(() => !document.hidden && api.get(`/price/chart?days=${days}`).then((r) => r.data.prices.length && setHist(r.data.prices)).catch(() => {}), 60000);
    return () => clearInterval(id);
  }, [load, days]);

  const first = data[0]?.price, last = data[data.length - 1]?.price;
  const pct = first ? ((last - first) / first) * 100 : 0;

  return (
    <div className="h-full" data-testid="price-chart-container">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#64748B]">Market</p>
          <h3 className="text-lg font-semibold tracking-tight">BTC / USD
            {status === "ok" && <span className={`ml-2 font-mono text-sm ${pct >= 0 ? "text-emerald-400" : "text-red-400"}`} data-testid="chart-range-change">{pct >= 0 ? "+" : ""}{pct.toFixed(2)}%</span>}
          </h3>
        </div>
        <div className="flex gap-1 rounded-xl border border-[#212638] bg-[#0F111A] p-1">
          {RANGES.map((r) => (
            <button key={r.k} onClick={() => setDays(r.k)} data-testid={`chart-range-${r.l.toLowerCase()}`}
              className={`rounded-lg px-3 py-1 text-xs font-semibold transition-colors ${days === r.k ? "bg-[#F7931A] text-[#08090E]" : "text-[#94A3B8] hover:text-white"}`}>
              {r.l}
            </button>
          ))}
        </div>
      </div>
      <div style={{ height: h }} className="relative">
        {status !== "ok" && (
          <div className="absolute inset-0 z-10 grid place-items-center" data-testid={`chart-${status}`}>
            {status === "loading" ? <Loader2 className="animate-spin text-[#F7931A]" size={28} /> : (
              <button onClick={load} data-testid="chart-retry-button" className="flex items-center gap-2 rounded-full border border-[#212638] px-4 py-2 text-xs text-[#94A3B8] transition-colors hover:bg-[#1C2030] hover:text-white">
                <RefreshCw size={13} /> Chart unavailable — retry
              </button>
            )}
          </div>
        )}
        <ResponsiveContainer width="100%" height={h}>
          <AreaChart data={data} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id="btcFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#F7931A" stopOpacity={0.4} />
                <stop offset="100%" stopColor="#F7931A" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis dataKey="t" hide />
            <YAxis domain={["auto", "auto"]} hide />
            <Tooltip content={<Tip />} />
            <Area type="monotone" dataKey="price" stroke="#F7931A" strokeWidth={2} fill="url(#btcFill)" isAnimationActive={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
